import React, { ReactNode, MouseEventHandler } from 'react';
import type { TagKind } from './Tag';

export type FilterChipKind = Extract<
  TagKind,
  'neutral' | 'inpatient' | 'outpatient' | 'icu' | 'ed' | 'exam' | 'protocols' | 'references'
>;

export type FilterChipProps = {
  active?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  kind?: FilterChipKind;
  count?: number;
};

type Palette = { activeBg: string; activeFg: string };

const PALETTES: Record<FilterChipKind, Palette> = {
  neutral: { activeBg: 'var(--fg-primary)', activeFg: 'var(--bg-surface)' },
  inpatient: { activeBg: 'var(--tag-inpatient-fg)', activeFg: '#fff' },
  outpatient: { activeBg: 'var(--tag-outpatient-fg)', activeFg: '#fff' },
  icu: { activeBg: 'var(--tag-icu-fg)', activeFg: '#fff' },
  ed: { activeBg: 'var(--tag-ed-fg)', activeFg: '#fff' },
  exam: { activeBg: 'var(--tag-exam-fg)', activeFg: '#fff' },
  protocols: { activeBg: 'var(--tag-protocols-fg)', activeFg: '#fff' },
  references: { activeBg: 'var(--tag-references-fg)', activeFg: '#fff' },
};

export function FilterChip({
  active,
  icon,
  children,
  onClick,
  kind = 'neutral',
  count,
}: FilterChipProps) {
  const p = PALETTES[kind] || PALETTES.neutral;
  return (
    <button
      aria-pressed={Boolean(active)}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        height: 34, padding: '0 14px',
        borderRadius: 'var(--r-pill)',
        background: active ? p.activeBg : 'var(--bg-surface)',
        color: active ? p.activeFg : 'var(--fg-primary)',
        border: `1px solid ${active ? 'transparent' : 'var(--line-border)'}`,
        fontSize: 14, fontWeight: 500, letterSpacing: '-0.16px',
        transition: 'all var(--dur-fast) var(--ease-out)',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
    >
      {icon}
      {children}
      {count != null && (
        <span
          style={{
            background: active ? 'rgba(255,255,255,.22)' : 'var(--bg-fill-1)',
            color: active ? 'currentColor' : 'var(--fg-tertiary)',
            borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 600,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export default FilterChip;
